import { useState, useEffect } from 'react';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import {
  Search, Target, Shield, AlertTriangle, Play, CheckCircle, Clock,
  ChevronRight, ChevronDown, FileText, Plus, Eye, XCircle, Loader2,
  Bug, Wifi, Users, Mail, Server, Database, Brain, Bookmark, Zap, Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const PLAYBOOK_ICONS = {
  ransomware: Bug,
  phishing: Mail,
  c2_detection: Wifi,
  insider_threat: Users
};

const SEVERITY_COLORS = {
  critical: { bg: 'bg-red-500/20', text: 'text-red-500', border: 'border-red-500/30' },
  high: { bg: 'bg-orange-500/20', text: 'text-orange-500', border: 'border-orange-500/30' },
  medium: { bg: 'bg-yellow-500/20', text: 'text-yellow-500', border: 'border-yellow-500/30' },
  low: { bg: 'bg-green-500/20', text: 'text-green-500', border: 'border-green-500/30' }
};

export default function ThreatHuntingPlaybooks() {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('playbooks');
  const [loading, setLoading] = useState(true);
  const [playbooks, setPlaybooks] = useState([]);
  const [investigations, setInvestigations] = useState([]);
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);
  const [selectedInvestigation, setSelectedInvestigation] = useState(null);
  const [showPlaybookDialog, setShowPlaybookDialog] = useState(false);
  const [showInvestigationDialog, setShowInvestigationDialog] = useState(false);
  const [showNewInvestigationDialog, setShowNewInvestigationDialog] = useState(false);
  const [startingInvestigation, setStartingInvestigation] = useState(false);
  
  // New investigation form
  const [newInvestigation, setNewInvestigation] = useState({
    playbook_key: '',
    title: '',
    description: ''
  });

  // Step completion form
  const [stepNotes, setStepNotes] = useState('');
  const [stepIocs, setStepIocs] = useState('');
  const [completingStep, setCompletingStep] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [playbooksRes, investigationsRes] = await Promise.all([
        api.get('/threat-hunting/playbooks'),
        api.get('/threat-hunting/investigations')
      ]);
      setPlaybooks(playbooksRes.data.playbooks || []);
      setInvestigations(investigationsRes.data.investigations || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewPlaybook = async (playbookKey) => {
    try {
      const response = await api.get(`/threat-hunting/playbooks/${playbookKey}`);
      setSelectedPlaybook(response.data);
      setShowPlaybookDialog(true);
    } catch (error) {
      toast.error('Failed to load playbook');
    }
  };

  const startInvestigation = async () => {
    if (!newInvestigation.playbook_key) {
      toast.error('Please select a playbook');
      return;
    }
    setStartingInvestigation(true);
    try {
      const response = await api.post('/threat-hunting/investigations', newInvestigation);
      toast.success('Investigation started');
      setShowNewInvestigationDialog(false);
      setNewInvestigation({ playbook_key: '', title: '', description: '' });
      fetchData();
      
      // Open the new investigation
      viewInvestigation(response.data.id);
    } catch (error) {
      toast.error('Failed to start investigation');
    } finally {
      setStartingInvestigation(false);
    }
  };

  const viewInvestigation = async (investigationId) => {
    try {
      const response = await api.get(`/threat-hunting/investigations/${investigationId}`);
      setSelectedInvestigation(response.data);
      setShowInvestigationDialog(true);
    } catch (error) {
      toast.error('Failed to load investigation');
    }
  };

  const completeStep = async (stepId) => {
    if (!selectedInvestigation) return;
    setCompletingStep(true);
    try {
      const iocs = stepIocs.split('\n').filter(i => i.trim()).map(ioc => {
        const parts = ioc.split(':');
        return {
          type: parts[0]?.trim() || 'unknown',
          value: parts[1]?.trim() || ioc.trim()
        };
      });
      
      await api.put(`/threat-hunting/investigations/${selectedInvestigation.id}/step`, {
        step_id: stepId,
        notes: stepNotes,
        iocs: iocs
      });
      
      toast.success('Step completed');
      setStepNotes('');
      setStepIocs('');
      
      // Refresh investigation
      viewInvestigation(selectedInvestigation.id);
      fetchData();
    } catch (error) {
      toast.error('Failed to complete step');
    } finally {
      setCompletingStep(false);
    }
  };

  const closeInvestigation = async (resolution) => {
    if (!selectedInvestigation) return;
    try {
      await api.put(`/threat-hunting/investigations/${selectedInvestigation.id}/close`, {
        status: 'closed',
        resolution: resolution
      });
      toast.success('Investigation closed');
      setShowInvestigationDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to close investigation');
    }
  };

  const addIocToInvestigation = async (type, value) => {
    if (!selectedInvestigation || !value.trim()) return;
    try {
      await api.post(`/threat-hunting/investigations/${selectedInvestigation.id}/ioc`, {
        type,
        value: value.trim()
      });
      toast.success('IOC added and enriched');
      viewInvestigation(selectedInvestigation.id);
    } catch (error) {
      toast.error('Failed to add IOC');
    }
  };

  const getPlaybookIcon = (key) => {
    const Icon = PLAYBOOK_ICONS[key] || Target;
    return Icon;
  };

  const activeInvestigations = investigations.filter(i => i.status === 'in_progress');
  const completedInvestigations = investigations.filter(i => i.status !== 'in_progress');

  return (
    <div className={`min-h-screen p-6 ${isDarkMode ? 'bg-[#0a0a0f] text-white' : 'bg-slate-50 text-slate-900'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={`text-2xl font-bold font-rajdhani ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            Threat Hunting Playbooks
          </h1>
          <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
            Step-by-step investigation guides with automated IOC collection
          </p>
        </div>
        <Dialog open={showNewInvestigationDialog} onOpenChange={setShowNewInvestigationDialog}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-500 text-white">
              <Play className="w-4 h-4 mr-2" />
              Start Investigation
            </Button>
          </DialogTrigger>
          <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
            <DialogHeader>
              <DialogTitle>Start New Investigation</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Select Playbook</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {playbooks.map((pb) => {
                    const Icon = getPlaybookIcon(pb.key);
                    const colors = SEVERITY_COLORS[pb.severity] || SEVERITY_COLORS.medium;
                    return (
                      <button
                        key={pb.key}
                        onClick={() => setNewInvestigation({...newInvestigation, playbook_key: pb.key, title: `Investigation - ${pb.name}`})}
                        className={`p-3 rounded-lg text-left transition-all border ${
                          newInvestigation.playbook_key === pb.key
                            ? `${colors.bg} ${colors.border}`
                            : isDarkMode ? 'bg-zinc-800/50 border-transparent hover:border-zinc-700' : 'bg-slate-50 border-transparent hover:border-slate-300'
                        }`}
                      >
                        <Icon className={`w-5 h-5 mb-1 ${colors.text}`} />
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {pb.name.replace(' Playbook', '')}
                        </p>
                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          {pb.steps_count} steps
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Investigation Title</Label>
                <Input
                  value={newInvestigation.title}
                  onChange={(e) => setNewInvestigation({...newInvestigation, title: e.target.value})}
                  placeholder="Enter investigation title"
                  className={isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}
                />
              </div>
              <div>
                <Label>Description (Optional)</Label>
                <Textarea
                  value={newInvestigation.description}
                  onChange={(e) => setNewInvestigation({...newInvestigation, description: e.target.value})}
                  placeholder="Describe the incident or reason for investigation..."
                  className={isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}
                  rows={3}
                />
              </div>
              <Button 
                onClick={startInvestigation} 
                disabled={startingInvestigation || !newInvestigation.playbook_key}
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white"
              >
                {startingInvestigation ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                Start Investigation
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-indigo-600/20' : 'bg-indigo-100'}`}>
              <Bookmark className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
            </div>
            <div>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{playbooks.length}</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Playbooks</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-orange-600/20' : 'bg-orange-100'}`}>
              <Zap className={`w-5 h-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
            </div>
            <div>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{activeInvestigations.length}</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Active Investigations</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-green-600/20' : 'bg-green-100'}`}>
              <CheckCircle className={`w-5 h-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
            </div>
            <div>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{completedInvestigations.length}</p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Completed</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-purple-600/20' : 'bg-purple-100'}`}>
              <Target className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
            </div>
            <div>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {investigations.reduce((acc, inv) => acc + (inv.iocs_collected?.length || 0), 0)}
              </p>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>IOCs Collected</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <TabsList className={`${isDarkMode ? 'bg-zinc-900/50 border border-white/10' : 'bg-slate-100 border border-slate-200'}`}>
            <TabsTrigger value="playbooks" className={`gap-2 ${isDarkMode ? 'data-[state=active]:bg-indigo-600 data-[state=active]:text-white' : ''}`}>
              <Bookmark className="w-4 h-4" />
              Playbooks
            </TabsTrigger>
            <TabsTrigger value="active" className={`gap-2 ${isDarkMode ? 'data-[state=active]:bg-indigo-600 data-[state=active]:text-white' : ''}`}>
              <Zap className="w-4 h-4" />
              Active ({activeInvestigations.length})
            </TabsTrigger>
            <TabsTrigger value="history" className={`gap-2 ${isDarkMode ? 'data-[state=active]:bg-indigo-600 data-[state=active]:text-white' : ''}`}>
              <Clock className="w-4 h-4" />
            History
          </TabsTrigger>
        </TabsList>
        
        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
            <Input
              placeholder="Search playbooks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-10 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}
            />
          </div>
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger className={`w-[140px] ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}>
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Severity" />
            </SelectTrigger>
            <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
              <SelectItem value="all">All Severities</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className={`w-[160px] ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}>
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="ransomware">Ransomware</SelectItem>
              <SelectItem value="phishing">Phishing</SelectItem>
              <SelectItem value="insider">Insider Threat</SelectItem>
              <SelectItem value="lateral">Lateral Movement</SelectItem>
              <SelectItem value="data_exfil">Data Exfiltration</SelectItem>
            </SelectContent>
          </Select>
          {activeTab === 'active' || activeTab === 'history' ? (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className={`w-[140px] ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
              </SelectContent>
            </Select>
          ) : null}
        </div>
      </div>

        {/* Playbooks Tab */}
        <TabsContent value="playbooks" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {playbooks
              .filter(pb => {
                const matchesSearch = searchTerm === '' || 
                  pb.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  pb.description?.toLowerCase().includes(searchTerm.toLowerCase());
                const matchesSeverity = severityFilter === 'all' || pb.severity === severityFilter;
                const matchesCategory = categoryFilter === 'all' || pb.key.includes(categoryFilter);
                return matchesSearch && matchesSeverity && matchesCategory;
              })
              .map((playbook) => {
              const Icon = getPlaybookIcon(playbook.key);
              const colors = SEVERITY_COLORS[playbook.severity] || SEVERITY_COLORS.medium;
              return (
                <div
                  key={playbook.key}
                  className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colors.bg}`}>
                        <Icon className={`w-6 h-6 ${colors.text}`} />
                      </div>
                      <div>
                        <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {playbook.name}
                        </h3>
                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          {playbook.category}
                        </p>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${colors.bg} ${colors.text}`}>
                      {playbook.severity}
                    </span>
                  </div>
                  <p className={`text-sm mb-4 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                    {playbook.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {playbook.steps_count} steps
                      </span>
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        ~{playbook.estimated_time}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewPlaybook(playbook.key)}
                        className={isDarkMode ? 'border-white/10' : ''}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          setNewInvestigation({ playbook_key: playbook.key, title: `Investigation - ${playbook.name}`, description: '' });
                          setShowNewInvestigationDialog(true);
                        }}
                        className="bg-indigo-600 hover:bg-indigo-500 text-white"
                      >
                        <Play className="w-3 h-3 mr-1" />
                        Start
                      </Button>
                    </div>
                  </div>
                  {playbook.mitre_tactics && playbook.mitre_tactics.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {playbook.mitre_tactics.map((tactic, idx) => (
                        <span key={idx} className={`text-[10px] px-2 py-0.5 rounded font-mono ${isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-700'}`}>
                          {tactic}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* Active Investigations Tab */}
        <TabsContent value="active" className="space-y-4">
          {activeInvestigations.length === 0 ? (
            <div className={`rounded-xl border p-12 text-center ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <Zap className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
              <p className={isDarkMode ? 'text-zinc-400' : 'text-slate-600'}>No active investigations</p>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Start an investigation from a playbook to begin threat hunting
              </p>
            </div>
          ) : (
            activeInvestigations.map((investigation) => {
              const progress = ((investigation.steps_completed?.length || 0) / (investigation.total_steps || 1)) * 100;
              const colors = SEVERITY_COLORS[investigation.severity] || SEVERITY_COLORS.medium;
              return (
                <div
                  key={investigation.id}
                  onClick={() => viewInvestigation(investigation.id)}
                  className={`rounded-xl border p-5 cursor-pointer transition-all ${isDarkMode ? 'bg-zinc-900/40 border-white/10 hover:border-indigo-500/30' : 'bg-white border-slate-200 hover:border-indigo-300'}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {investigation.title}
                      </h3>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {investigation.playbook_name} • Started {new Date(investigation.started_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${colors.bg} ${colors.text}`}>
                      {investigation.severity}
                    </span>
                  </div>
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                        Step {investigation.current_step} of {investigation.total_steps}
                      </span>
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                        {investigation.iocs_collected?.length || 0} IOCs
                      </span>
                      <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-700'}`}>
                        {investigation.evidence?.length || 0} Evidence
                      </span>
                    </div>
                    <ChevronRight className={`w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                  </div>
                </div>
              );
            })
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          {completedInvestigations.length === 0 ? (
            <div className={`rounded-xl border p-12 text-center ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <Clock className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
              <p className={isDarkMode ? 'text-zinc-400' : 'text-slate-600'}>No completed investigations</p>
            </div>
          ) : (
            completedInvestigations.map((investigation) => (
              <div
                key={investigation.id}
                onClick={() => viewInvestigation(investigation.id)}
                className={`rounded-xl border p-5 cursor-pointer transition-all ${isDarkMode ? 'bg-zinc-900/40 border-white/10 hover:border-indigo-500/30' : 'bg-white border-slate-200 hover:border-indigo-300'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <div>
                      <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {investigation.title}
                      </h3>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        Closed {investigation.closed_at ? new Date(investigation.closed_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      {investigation.iocs_collected?.length || 0} IOCs collected
                    </span>
                    <ChevronRight className={`w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                  </div>
                </div>
              </div>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Playbook Detail Dialog */}
      <Dialog open={showPlaybookDialog} onOpenChange={setShowPlaybookDialog}>
        <DialogContent className={`max-w-3xl max-h-[90vh] overflow-auto ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
              {selectedPlaybook?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedPlaybook && (
            <div className="space-y-4">
              <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                {selectedPlaybook.description}
              </p>
              
              <div className="flex flex-wrap gap-2">
                <span className={`text-xs px-2 py-1 rounded ${SEVERITY_COLORS[selectedPlaybook.severity]?.bg} ${SEVERITY_COLORS[selectedPlaybook.severity]?.text}`}>
                  {selectedPlaybook.severity}
                </span>
                <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-700'}`}>
                  {selectedPlaybook.category}
                </span>
                <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-700'}`}>
                  ~{selectedPlaybook.estimated_time}
                </span>
              </div>

              {selectedPlaybook.mitre_tactics && (
                <div>
                  <p className={`text-xs mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>MITRE ATT&CK</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedPlaybook.mitre_tactics.map((tactic, idx) => (
                      <span key={idx} className={`text-xs px-2 py-1 rounded font-mono ${isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-700'}`}>
                        {tactic}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Investigation Steps ({selectedPlaybook.steps?.length || 0})
                </h4>
                {selectedPlaybook.steps?.map((step, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-zinc-800/50 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isDarkMode ? 'bg-indigo-600/30 text-indigo-400' : 'bg-indigo-100 text-indigo-700'}`}>
                        {step.id}
                      </span>
                      <h5 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {step.title}
                      </h5>
                    </div>
                    <p className={`text-sm mb-3 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                      {step.description}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Actions</p>
                        <ul className={`text-xs space-y-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                          {step.actions?.slice(0, 3).map((action, aidx) => (
                            <li key={aidx} className="flex items-start gap-1">
                              <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              {action}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Automated Checks</p>
                        <ul className={`text-xs space-y-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                          {step.automated_checks?.map((check, cidx) => (
                            <li key={cidx} className="flex items-start gap-1">
                              <Brain className="w-3 h-3 mt-0.5 flex-shrink-0 text-purple-500" />
                              {check}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Investigation Detail Dialog */}
      <Dialog open={showInvestigationDialog} onOpenChange={setShowInvestigationDialog}>
        <DialogContent className={`max-w-4xl max-h-[90vh] overflow-auto ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className={`w-5 h-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
              {selectedInvestigation?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedInvestigation && (
            <div className="space-y-4">
              {/* Progress */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                    Progress: Step {selectedInvestigation.current_step} of {selectedInvestigation.total_steps}
                  </span>
                  <span className={`text-sm font-bold ${selectedInvestigation.status === 'completed' ? 'text-green-500' : isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {selectedInvestigation.status === 'completed' ? 'Completed' : `${Math.round((selectedInvestigation.steps_completed?.length || 0) / selectedInvestigation.total_steps * 100)}%`}
                  </span>
                </div>
                <Progress value={(selectedInvestigation.steps_completed?.length || 0) / selectedInvestigation.total_steps * 100} className="h-2" />
              </div>

              {/* Steps */}
              {selectedInvestigation.playbook && (
                <div className="space-y-3">
                  {selectedInvestigation.playbook.steps?.map((step) => {
                    const isCompleted = selectedInvestigation.steps_completed?.some(s => s.step_id === step.id);
                    const isCurrent = step.id === selectedInvestigation.current_step;
                    return (
                      <div 
                        key={step.id} 
                        className={`p-4 rounded-lg border ${
                          isCompleted 
                            ? isDarkMode ? 'bg-green-900/20 border-green-500/30' : 'bg-green-50 border-green-200'
                            : isCurrent 
                              ? isDarkMode ? 'bg-indigo-900/20 border-indigo-500/30' : 'bg-indigo-50 border-indigo-200'
                              : isDarkMode ? 'bg-zinc-800/50 border-white/5' : 'bg-slate-50 border-slate-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            {isCompleted ? (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : isCurrent ? (
                              <Play className="w-5 h-5 text-indigo-500" />
                            ) : (
                              <span className={`w-5 h-5 rounded-full border-2 ${isDarkMode ? 'border-zinc-600' : 'border-slate-300'}`} />
                            )}
                            <h5 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              Step {step.id}: {step.title}
                            </h5>
                          </div>
                          {isCurrent && selectedInvestigation.status !== 'completed' && (
                            <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-700'}`}>
                              Current
                            </span>
                          )}
                        </div>

                        {isCurrent && selectedInvestigation.status !== 'completed' && (
                          <div className="mt-3 space-y-3">
                            <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                              {step.description}
                            </p>
                            <div>
                              <Label className="text-xs">Notes for this step</Label>
                              <Textarea
                                value={stepNotes}
                                onChange={(e) => setStepNotes(e.target.value)}
                                placeholder="Document your findings..."
                                className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}
                                rows={2}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">IOCs Found (one per line, format: type:value)</Label>
                              <Textarea
                                value={stepIocs}
                                onChange={(e) => setStepIocs(e.target.value)}
                                placeholder="ip:192.168.1.1&#10;hash:abc123..."
                                className={`mt-1 font-mono text-xs ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}
                                rows={2}
                              />
                            </div>
                            <Button
                              onClick={() => completeStep(step.id)}
                              disabled={completingStep}
                              className="bg-green-600 hover:bg-green-500 text-white"
                            >
                              {completingStep ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                              Complete Step
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Collected IOCs */}
              {selectedInvestigation.iocs_collected && selectedInvestigation.iocs_collected.length > 0 && (
                <div>
                  <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Collected IOCs ({selectedInvestigation.iocs_collected.length})
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {selectedInvestigation.iocs_collected.map((ioc, idx) => (
                      <div key={idx} className={`p-2 rounded ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-700'}`}>
                            {ioc.type}
                          </span>
                          <code className={`text-xs truncate ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                            {ioc.value}
                          </code>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Close Investigation */}
              {selectedInvestigation.status === 'in_progress' && (
                <div className="flex gap-2 pt-4 border-t border-white/10">
                  <Button
                    onClick={() => closeInvestigation('Resolved - Threat neutralized')}
                    className="bg-green-600 hover:bg-green-500 text-white"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Close as Resolved
                  </Button>
                  <Button
                    onClick={() => closeInvestigation('False positive')}
                    variant="outline"
                    className={isDarkMode ? 'border-white/10' : ''}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Close as False Positive
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
